import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon from "@mdi/react";
import { mdiChevronLeft } from "@mdi/js";
import EmailCard from "./EmailCard";
import {
  fetchEmailAutomation,
  sendEmailNow,
  selectEmailAutomationByJobId,
  selectCalendarLoading,
  selectCalendarErrors,
} from "../slice";
import { computeSummaryFromLogs } from "../utils/emailAutomationHelpers";
import { compiledReportUrl } from "utils/helperFunctions/compiledReportUrl";
import "./EmailActivityPanel.css";

const EmailActivityPanel = ({
  appointment,
  onBack,
  onCopyContent,
  onContactForHelp,
  onSummaryUpdate,
}) => {
  const dispatch = useDispatch();
  const jobId = appointment?.job?.id;
  const logs = useSelector(selectEmailAutomationByJobId(jobId));
  const loading = useSelector(selectCalendarLoading);
  const errors = useSelector(selectCalendarErrors);
  const [sendingLogIds, setSendingLogIds] = useState(new Set());

  useEffect(() => {
    if (jobId) {
      dispatch(fetchEmailAutomation(jobId));
    }
  }, [dispatch, jobId]);

  const handleSendNow = async (logId) => {
    if (!jobId || sendingLogIds.has(logId)) return;
    setSendingLogIds((prev) => new Set([...prev, logId]));
    try {
      await dispatch(sendEmailNow({ jobId, logId })).unwrap();
      dispatch(fetchEmailAutomation(jobId));
    } catch (error) {
      // Error is handled via Redux state
    } finally {
      setSendingLogIds((prev) => {
        const next = new Set(prev);
        next.delete(logId);
        return next;
      });
    }
  };

  const handleCopyContent = (email) => {
    if (onCopyContent) {
      onCopyContent(email.rendered_subject, email.rendered_body);
    }
  };

  const handleContactForHelp = () => {
    if (onContactForHelp) {
      onContactForHelp();
    }
  };

  const handleBack = () => {
    if (onSummaryUpdate && logs.length > 0) {
      onSummaryUpdate(computeSummaryFromLogs(logs));
    }
    onBack();
  };

  const handleViewReport = () => {
    const compiledReport = appointment?.job?.report?.compiled_report;
    if (compiledReport?.id) {
      window.open(
        compiledReportUrl(compiledReport.id),
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  return (
    <div className="email-activity-panel">
      <div
        className="email-activity-header"
        onClick={handleBack}
        role="button"
        tabIndex={0}
        aria-label="Go back"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBack();
          }
        }}
      >
        <Icon
          path={mdiChevronLeft}
          size={1}
          className="email-activity-back-icon"
        />
        <span className="email-activity-title">Email Automation</span>
      </div>

      <div className="email-activity-list">
        {loading.emailAutomation ? (
          <div className="email-activity-loading">
            <div
              className="spinner-border spinner-border-sm text-primary"
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <span>Loading email status...</span>
          </div>
        ) : errors.emailAutomation ? (
          <div className="email-activity-error">
            <span>Failed to load email automation data.</span>
            <button
              className="btn btn-sm btn-outline-primary mt-2"
              onClick={() => jobId && dispatch(fetchEmailAutomation(jobId))}
            >
              Retry
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="email-activity-empty">
            No email automation configured for this job.
          </div>
        ) : (
          logs.map((log) => (
            <EmailCard
              key={log.id}
              email={log}
              onCopyContent={handleCopyContent}
              onSendNow={handleSendNow}
              onContactForHelp={handleContactForHelp}
              onViewReport={handleViewReport}
              isSending={sendingLogIds.has(log.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default EmailActivityPanel;
